import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolSite,
  ProtocolWarning,
} from "../types";

/**
 * Dalteparin — Fragmin, the other low-molecular-weight heparin, and the
 * syringe whose needle is bent into a plastic trap when she is done.
 *
 * ### Two labels, one procedure
 *
 * The UK Fragmin leaflet (emc, Pfizer) and the French Fragmine notice
 * (ANSM) carry the same eight-step self-injection section, nearly word for
 * word: bend the plastic needle catcher away, pull the grey rubber cover
 * straight off; **the air bubble is supposed to be there and is not pushed
 * out**, or some of the dose is lost; a different site each time, never
 * into tender, bruised, red or hard skin, never into a scar; sitting where
 * she can see her stomach, or lying down; a fold of skin between thumb and
 * forefinger, the syringe like a pencil; **at a right angle, vertically and
 * not at an angle**, the needle fully in; the plunger slow and steady with
 * the fold held, then release and out; gentle pressure, a cotton ball for
 * ten seconds, **no rubbing**; the needle pivoted into the catcher until it
 * clicks and bent past 45 degrees; syringe to the sharps container. The
 * English copy is the UK leaflet's and the French the French notice's.
 *
 * ### Where they differ
 *
 * The UK leaflet offers a U-shaped area around the navel or the side of the
 * middle thighs to anyone; the French notice names the abdomen for an adult
 * and keeps the thigh for a child. The walk asks, as the UK leaflet allows,
 * and the fiche flags it. The French notice's "forgotten dose" and side
 * effects are terser than the UK's: the "tell your doctor" and the allergic
 * stop line are the UK leaflet's, read into French by the author.
 *
 * ### The unit
 *
 * International units of anti-Xa activity and nothing else, on both boxes.
 * No strength list: the fertility prescription is usually the 5 000 IU
 * syringe once a day, but the box exists from 2 500 to 18 000.
 *
 * Restated from `content/data/medications/anticoagulants.json` (the
 * `Fragmin` entry), whose text was a disclaimer with no steps until it was
 * written from these two labels.
 */

const DALTEPARIN_BRANDS = [
  { name: "Fragmin", markets: ["GB", "EU"] },
  { name: "Fragmine", markets: ["FR"] },
] as const satisfies readonly ProtocolBrand[];

/** No double dose on both labels; "tell your doctor" is the UK leaflet's. */
const MISSED_DOSE: ProtocolWarning = {
  id: "dalteparin.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed dose - A double dose should not be given to make up for a forgotten dose. Tell your doctor or pharmacist if you think that a dose has been forgotten.",
    fr: "Dose oubliée - Ne prenez pas de dose double pour compenser la dose que vous avez oublié de prendre. Prévenez votre médecin ou votre pharmacien si vous pensez avoir oublié une dose.",
  },
  sourceSegment:
    "⚠ Missed dose - A double dose should not be given to make up for a forgotten dose. Tell your doctor or pharmacist if you think that a dose has been forgotten.",
};

/** The UK leaflet's "stop and talk to a doctor at once", routed to emergency. */
const ALLERGIC_REACTION: ProtocolWarning = {
  id: "dalteparin.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Stop and call emergency services straight away for any signs of a severe allergic reaction, such as difficulty breathing or swelling of the lips, mouth, throat or eyes.",
    fr: "Aide urgente - Arrêtez et appelez les secours tout de suite en cas de signes de réaction allergique grave, comme une difficulté à respirer ou un gonflement des lèvres, de la bouche, de la gorge ou des yeux.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Stop and call emergency services straight away for any signs of a severe allergic reaction, such as difficulty breathing or swelling of the lips, mouth, throat or eyes.",
};

/**
 * Bleeding to the clinic now, and the French notice's own reassurance that
 * the bruise and the little lump are expected and not a reason to stop.
 */
const BLEEDING: ProtocolWarning = {
  id: "dalteparin.bleeding",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Bleeding - Tell your doctor or nurse immediately about any bleeding, visible or not, and about bruising more easily than usual. Bruises and small lumps under the skin at the injection site are common, disappear on their own and are not a reason to stop.",
    fr: "Saignement - Avertissez immédiatement votre médecin ou l'infirmière de tout saignement, extériorisé ou non, et de bleus plus faciles que d'habitude. Les hématomes et les petites boules sous la peau au point d'injection sont fréquents, disparaissent spontanément et ne doivent pas faire interrompre le traitement.",
  },
  sourceSegment:
    "⚠ Bleeding - Tell your doctor or nurse immediately about any bleeding, visible or not, and about bruising more easily than usual. Bruises and small lumps under the skin at the injection site are common, disappear on their own and are not a reason to stop.",
};

/** The UK leaflet's two shaded areas; the French notice keeps the thigh for a child. */
const SITES: readonly ProtocolSite[] = [
  {
    id: "abdomen",
    label: { en: "Abdomen", fr: "Ventre" },
    copy: {
      en: "Choose and clean the site - Inject into the U-shaped area around your navel, here on your abdomen, using a different site each time. Do not inject where the skin is tender, bruised, red or hard, and avoid scars. Wash and dry your hands, clean the site with a new alcohol swab using a circular motion, let the skin dry thoroughly and do not touch the area again before the injection.",
      fr: "Choisir et désinfecter le site - Injectez dans la zone en forme de U autour du nombril, ici sur le ventre, en utilisant un site d'injection différent à chaque fois. N'effectuez pas l'injection dans des zones où la peau est sensible, meurtrie, rouge ou dure, et évitez les cicatrices. Lavez-vous les mains et séchez-les, nettoyez le site avec un nouveau tampon imbibé d'alcool en effectuant un mouvement circulaire, laissez la peau sécher complètement et ne touchez plus cette zone avant l'injection.",
    },
  },
  {
    id: "thigh",
    label: { en: "Side of thigh", fr: "Côté de la cuisse" },
    copy: {
      en: "Choose and clean the site - Inject into the side of your middle thigh, here, using a different site each time. Do not inject where the skin is tender, bruised, red or hard, and avoid scars. Wash and dry your hands, clean the site with a new alcohol swab using a circular motion, let the skin dry thoroughly and do not touch the area again before the injection.",
      fr: "Choisir et désinfecter le site - Injectez sur le côté de la partie médiane de la cuisse, ici, en utilisant un site d'injection différent à chaque fois. N'effectuez pas l'injection dans des zones où la peau est sensible, meurtrie, rouge ou dure, et évitez les cicatrices. Lavez-vous les mains et séchez-les, nettoyez le site avec un nouveau tampon imbibé d'alcool en effectuant un mouvement circulaire, laissez la peau sécher complètement et ne touchez plus cette zone avant l'injection.",
    },
  },
];

export const DALTEPARIN_PROTOCOL: InjectionProtocol = {
  medicationSlug: "dalteparin",
  brands: DALTEPARIN_BRANDS,
  aliases: ["Dalteparin", "Daltéparine"],
  indication: "anticoagulant",
  dose: { unit: "IU" },
  form: "pre_filled_syringe",
  prescriptionForms: ["pre_filled_syringe"],
  route: "subcutaneous",
  version: "2026-10-05.1",
  derivedFrom: "content/data/medications/anticoagulants.json#dalteparin",
  sites: SITES,
  steps: [
    {
      id: "dalteparin.syringe.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Follow these steps only after you have been trained by your doctor, and inject at the times your doctor recommended; if you are not sure what to do, talk to your doctor first. Lay out your prefilled syringe of {brand}, a new alcohol swab, a cotton ball and a sharps container. {brand} is given under the skin, usually once a day; it must not be injected into a muscle.",
        fr: "Préparer - Ne suivez ces étapes qu'après avoir été formée par votre médecin, et injectez aux moments recommandés par votre médecin ; si vous n'êtes pas sûre de ce que vous devez faire, parlez-en d'abord à votre médecin. Disposez votre seringue préremplie de {brand}, un nouveau tampon imbibé d'alcool, un tampon de coton et un collecteur. {brand} s'injecte sous la peau, en général une fois par jour ; il ne doit pas être injecté dans un muscle.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      id: "dalteparin.syringe.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: SITES[0]!.copy,
    },
    {
      // The bubble stays, as on the enoxaparin syringe two shelves over —
      // and the dose is named here, where losing some of it is the risk.
      id: "dalteparin.syringe.ready-the-syringe",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Ready the syringe - Sit comfortably where you can see your stomach, or lie down. Pick up the syringe, grasp the tip of the plastic needle catcher and bend it away from the needle shield, then remove the grey rubber cover by pulling it straight off. You will notice an air bubble in the syringe: it is supposed to be there and you can ignore it. Do not press the plunger yet and do not push the bubble out, as some of the medicine would be lost and your dose{dose} reduced.",
        fr: "Préparer la seringue - Installez-vous assise confortablement de façon à voir votre ventre, ou allongez-vous. Prenez la seringue, saisissez la pointe du récupérateur d'aiguille en plastique et pliez-le pour l'éloigner de la protection, puis retirez le capuchon en caoutchouc gris en le tirant tout droit. Vous remarquerez une bulle d'air dans la seringue : elle est censée être là et vous pouvez simplement l'ignorer. N'appuyez pas encore sur le piston et ne chassez pas la bulle, car une partie du médicament serait perdue et votre dose{dose} diminuée.",
      },
    },
    {
      // "At a right angle, vertically and not at an angle" is the label's
      // own phrase for 90°; the figure keeps its mark.
      id: "dalteparin.syringe.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - With your thumb and forefinger, lift up a fold of skin with one hand and hold the syringe like a pencil in the other. Hold the syringe above the folded skin at a right angle, vertically and not at an angle, and insert the needle into the skin until it is fully inserted. Push the plunger all the way down at a slow, steady rate, keeping the fold pinched while you inject, then release the fold and pull the needle out.",
        fr: "Injecter - Avec le pouce et l'index, soulevez un pli cutané d'une main et tenez la seringue comme un crayon de l'autre. Tenez la seringue au-dessus de la peau pliée en la maintenant à angle droit, verticalement et non en biais, et insérez l'aiguille dans la peau jusqu'à ce qu'elle soit complètement insérée. Poussez le piston à fond, à un rythme lent et régulier, en continuant à pincer le pli cutané pendant l'injection, puis relâchez le pli et retirez l'aiguille.",
      },
      warnings: [ALLERGIC_REACTION],
    },
    {
      // The Needle-Trap: the one safety system in the catalogue she works
      // herself, after the needle is out, against a table.
      id: "dalteparin.syringe.remove-the-needle",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Lock the needle - If there is any oozing of blood, apply gentle pressure; press a cotton ball over the site for 10 seconds and do not rub it, as rubbing may encourage bruising. Then place the plastic catcher against a hard, stable surface and, with one hand, pivot the syringe barrel upwards against the needle, forcing the needle into the catcher until it locks in place with a click. Keep bending until the syringe is well past 45 degrees from the surface, so that it can never be used again.",
        fr: "Verrouiller l'aiguille - S'il y a un écoulement de sang, exercez une légère pression ; pressez un tampon de coton sur le site pendant 10 secondes et ne frottez pas, car cela pourrait favoriser un hématome. Placez ensuite le dispositif de protection en plastique contre une surface dure et stable et, d'une main, faites pivoter le corps de la seringue vers le haut contre l'aiguille pour l'introduire dans le dispositif jusqu'au clic. Continuez à plier jusqu'à ce que la seringue dépasse nettement 45 degrés par rapport à la surface, pour la rendre définitivement inutilisable.",
      },
    },
    {
      id: "dalteparin.syringe.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - You may place a bandage over the site. Put the syringe and needle into your sharps container and keep the container out of reach of other people; when it is almost full, dispose of it as instructed or speak to your doctor or nurse.",
        fr: "Après l'injection - Vous pouvez placer un pansement sur le site d'injection. Jetez la seringue et l'aiguille dans votre collecteur et gardez-le hors de portée des autres personnes ; lorsqu'il est presque plein, jetez-le en suivant les instructions ou parlez-en à votre médecin ou à votre infirmière.",
      },
      warnings: [BLEEDING],
    },
  ],
};
